import { type useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useDismissChatDetectedChartingCredentials } from "@src/appV2/redesign/Charting/api/useDismissChatDetectedChartingCredentials";
import {
  invalidateChatDetectedChartingCredentials,
  useGetChatDetectedChartingCredentials,
} from "@src/appV2/redesign/Charting/api/useGetChatDetectedChartingCredentials";
import { type ChartingCredential } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { useGetChartingCredentials } from "@src/appV2/Shifts/Shift/useGetChartingCredentials";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";

import { DetectedChartingCredentialsCard } from "./DetectedChartingCredentialsCard";
import { UpdateChartingCredentialsContainer } from "./UpdateChartingCredentialsContainer";

export interface DetectedChartingCredentialsCardContainerProps {
  workplaceId: string;
  updateCredentialsModalState: ReturnType<typeof useModalState>;
}

export function DetectedChartingCredentialsCardContainer(
  props: DetectedChartingCredentialsCardContainerProps
) {
  const { workplaceId, updateCredentialsModalState } = props;
  const worker = useDefinedWorker();
  const queryClient = useQueryClient();

  const {
    data: chatDetectedChartingCredentials,
    isSuccess: isSuccessChatDetectedChartingCredentials,
    isLoading: isLoadingChatDetectedChartingCredentials,
  } = useGetChatDetectedChartingCredentials(
    { workerId: worker.userId, workplaceId, status: "pending" },
    { enabled: isDefined(worker.userId) && isDefined(workplaceId) }
  );

  const { data: chartingCredentialsResponse, isLoading: isLoadingChartingCredentials } =
    useGetChartingCredentials(worker.userId, workplaceId, {
      enabled: isDefined(worker.userId) && isDefined(workplaceId),
    });

  const [chartingCredentialsResponseData] = chartingCredentialsResponse?.data ?? [];
  const { chartingCredentials } = chartingCredentialsResponseData?.attributes ?? {};
  const hasExistingChartingCredentials =
    !isLoadingChartingCredentials && isDefined(chartingCredentials);

  const { mutateAsync: dismissChatDetectedChartingCredentials } =
    useDismissChatDetectedChartingCredentials();

  const [detectedCredential] = chatDetectedChartingCredentials?.data ?? [];
  const detectedChartingCredentials: ChartingCredential | undefined = detectedCredential
    ? {
        username: detectedCredential.attributes.username,
        password: detectedCredential.attributes.password,
        software: undefined,
      }
    : undefined;

  const handleDismiss = async () => {
    if (!detectedCredential) {
      return;
    }

    logEvent(APP_V2_USER_EVENTS.CHAT_DETECTED_CHARTING_CREDENTIALS_DISMISSED, {
      workerId: worker.userId,
      workplaceId,
    });

    await dismissChatDetectedChartingCredentials({
      data: {
        type: "chat-detected-charting-credentials",
        id: detectedCredential.id,
        attributes: {
          state: "dismissed",
        },
      },
    });

    await invalidateChatDetectedChartingCredentials(queryClient, {
      workerId: worker.userId,
      workplaceId,
      status: "pending",
    });
  };

  const handleUpdateCredentials = () => {
    logEvent(APP_V2_USER_EVENTS.CHAT_DETECTED_CHARTING_CREDENTIALS_UPDATE_BUTTON_CLICKED, {
      workerId: worker.userId,
      workplaceId,
    });
    updateCredentialsModalState.openModal();
  };

  const hasPendingChatDetectedChartingCredentials =
    !isLoadingChatDetectedChartingCredentials &&
    isSuccessChatDetectedChartingCredentials &&
    chatDetectedChartingCredentials?.data.length > 0;

  const shouldShowCard = hasPendingChatDetectedChartingCredentials && !isLoadingChartingCredentials;

  if (!shouldShowCard) {
    return null;
  }

  return (
    <>
      <DetectedChartingCredentialsCard
        hasExistingChartingCredentials={hasExistingChartingCredentials}
        onDismiss={handleDismiss}
        onUpdateCredentials={handleUpdateCredentials}
      />
      <UpdateChartingCredentialsContainer
        showPassword
        modalState={updateCredentialsModalState}
        workplaceId={workplaceId}
        workerId={worker.userId}
        chartingCredentials={chartingCredentials}
        defaultCredentials={detectedChartingCredentials}
        source="chat-detected-and-saved"
      />
    </>
  );
}
